import { SortOrder } from '@els/els-react--table';

// Single dataviz accent for every summary chart panel.
export const CHART_COLOR = '#548fe8';

// Summary chart panels. `topReagents`/`topSolvents` arrive from the service as
// { Tight, Medium, Wide } buckets and render the Tight/Medium/Wide tabs;
export const CHART_CONFIGS = [
  { key: 'topReagents', heading: 'Top reagents & catalysts', a11yTitle: 'Top reagents and catalysts', hasTabs: true },
  { key: 'topSolvents', heading: 'Top solvents', a11yTitle: 'Top solvents', hasTabs: true },
];

export const SUMMARY_CARD_TOOLTIP =
  'How often each reagent, catalyst and solvent appears across all supporting reactions similar to your query.<br/> Each tab (Tight / Medium / Wide) shows the summary at that similarity level.';

// Tab order for the summary charts. Keys match the buckets produced by
// ConditionsService.normalizeSummary (AR-3.1).
export const SUMMARY_TABS = ['Tight', 'Medium', 'Wide'];

export const SIMILARITY_LEVEL_STYLES = {
  tight: { pillColor: 'confirm', textColor: 'white' },
  medium: { pillColor: 'alert', textColor: 'text' },
  wide: { pillColor: 'warn', textColor: 'white' },
};

export const EMPTY_SUMMARY = {
  topReagents: { Tight: [], Medium: [], Wide: [] },
  topSolvents: { Tight: [], Medium: [], Wide: [] },
  matchDistribution: [],
};

export const HEADER_METRIC_CONFIGS = [
  {
    key: 'sets',
    label: 'Condition sets',
    valueKey: 'conditionSets',
    modifier: 'sets',
    tooltip:
      'Distinct reagent, catalyst and solvent combinations found in reactions similar to the query reaction. Currently showing up to 20 condition sets.',
  },
  {
    key: 'reactions',
    label: 'Examples found',
    valueKey: 'uniqueReactions',
    modifier: 'reactions',
    tooltip: 'Total number of supporting reactions across all condition sets shown.',
  },
];

// Column metadata for the Leyden Table. `tooltip` text is rendered into an
// info-icon Flyout by the component; keeping it here avoids inline object
// literals inside JSX (AR-3.3).
export const CONDITION_COLUMN_DEFS = [
  {
    value: 'rank',
    label: 'Rank',
    tooltip:
      'Sorted by similarity to your query (Tight -> Medium -> Wide), then by number of supporting reactions within each level - more precedent, higher rank.',
  },
  { value: 'reagentsCatalysts', label: 'Reagents & catalysts' },
  { value: 'solvents', label: 'Solvents', className: 'conditions-detail__table-cell-solvents' },
  {
    value: 'rcLevel',
    label: 'Similarity level',
    tooltip:
      "How closely precedents match your query, based on how much of the reaction centre's surroundings must match. More surroundings matched = more similar precedents, but usually fewer examples.<br/> <b>Tight</b> - most similar - reaction centre + up to its second environment (alpha- and beta-atoms).<br/> <b>Medium</b> - moderately similar - reaction centre + its first environment (alpha-atoms).<br /> <b>Wide</b> - least similar - reaction centre only.",
  },
  {
    value: 'yieldPercent',
    label: 'Median yield',
    tooltip: 'Median reported yield for the similar reactions using this reagent, catalyst and solvent combination.',
  },
  {
    value: 'reactionCount',
    label: 'Number of reactions',
    tooltip: 'Number of similar Reaxys reactions matching this reagent, catalyst and solvent combination. Click to open them in Reaxys.',
  },
  {
    value: 'fgLability',
    label: 'Functional group reactivity reference',
    tooltip:
      'How functional groups outside the reaction centre behaved under this reagent, catalyst and solvent combination. For groups in your query that stay unchanged, it shows the percentage that reacted vs. stayed intact, plus the total number of examples in Reaxys. Based on conditions alone; reaction similarity is not considered.',
    className: 'conditions-detail__table-cell-fg',
  },
];

// Only numeric columns are meaningfully sortable — other cells hold JSX nodes.
export const SORTABLE_COLUMNS = { rank: true, reactionCount: true, yieldPercent: true };

export const DEFAULT_SORT_BY = 'rank';
export const DEFAULT_SORT_ORDER = SortOrder.ASC;
